/**
 * The three-path rental domain model.
 *
 * Implements THREE-PATH-RENTAL-IMPLEMENTATION-PLAN.md §1 and the ADR's
 * decisions D3/D4/D7 on the tenant side. The one rule everything here exists
 * to enforce:
 *
 *   If the tenant enters a price, the request is COMPETITIVE.
 *   If the tenant accepts a provider's displayed price, the request is DIRECT.
 *
 * The plan says the UI and protocol must not blur that boundary, and blurring
 * it is not a cosmetic failure. A tenant who set a maximum price and believed
 * they had bought a specific host at a fixed rate has been told two untrue
 * things at once: the price was a cap, not a promise, and no host was bound.
 *
 * So the mode is DERIVED here rather than chosen by a screen, and the
 * derivation is a pure function with tests. A component that could set the
 * mode independently of the price would be able to produce the blurred state
 * this module exists to make unrepresentable.
 */

/** Mirrors `ddws.rental.v1.RentalMode`. The strings are the wire values. */
export type RentalMode =
  | 'RENTAL_MODE_DIRECT_CUSTOM'
  | 'RENTAL_MODE_DIRECT_HOST_EXCLUSIVE'
  | 'RENTAL_MODE_DIRECT_HOST_AVAILABLE'
  | 'RENTAL_MODE_COMPETITIVE'

/**
 * How the price on the review screen was arrived at.
 *
 * Recorded separately from the mode because the tenant-facing consequence
 * differs: a provider-quoted price is what they will pay, a tenant cap is a
 * ceiling they may pay less than, and a reference price is neither — it is an
 * indication that binds nobody. Rendering all three as "price" is how a
 * console ends up promising something no component intended to promise.
 */
export type PricingSource =
  /** A signed, expiring provider quote. Exactly this amount, this provider. */
  | 'provider-quote'
  /** The tenant's own maximum. Eligible providers may bid at or below it. */
  | 'tenant-cap'
  /** A provider's indicative "from" figure. Binds nobody. */
  | 'reference'

/**
 * Every rental product still understood by the console and protocol.
 *
 * `host-available` remains in this compatibility list so existing links and
 * already-created shared-host rentals can still be handled. It is hidden from
 * the new-rental chooser; URL-routed configuration and review keep supporting
 * the mode for compatibility. See `RENTAL_CHOOSER_PATHS` below.
 */
export const RENTAL_PATHS = [
  'host-exclusive',
  'host-available',
  'custom-direct',
  'competitive',
] as const

export type RentalPath = (typeof RENTAL_PATHS)[number]

/** The two products exposed by the rental front door. */
export const RENTAL_CHOOSER_PATHS = [
  'host-available',
  'competitive',
] as const

export interface RentalPathCopy {
  /** The action label. */
  title: string
  /** One line under the title. */
  summary: string
  /**
   * The guarantee, stated so a tenant can tell these apart at a glance.
   *
   * Plan §8: no UI copy may claim host binding, fixed price, exclusivity or
   * current availability until the signed protocol and provider lock enforce
   * it. These strings are written to be true of what the protocol actually
   * does, which is why "all currently available capacity" says *shared* in as
   * many words — it is the one a tenant is most likely to mistake for
   * exclusivity.
   */
  guarantee: string
  mode: RentalMode
  pricing: PricingSource
}

export const RENTAL_PATH_COPY: Record<RentalPath, RentalPathCopy> = {
  'host-exclusive': {
    title: 'Reserve an entire host',
    summary: "One physical machine, reserved for you alone.",
    guarantee:
      'No other tenant runs on this machine while your lease is active. You are billed for the whole host at the price shown.',
    mode: 'RENTAL_MODE_DIRECT_HOST_EXCLUSIVE',
    pricing: 'provider-quote',
  },
  'host-available': {
    title: "Take a host's free capacity",
    summary: 'The capacity that is free on one machine right now.',
    guarantee:
      'Your workload runs on one specific machine, and this is a shared machine — other tenants may already be on it and more may join. You are billed for the capacity shown.',
    mode: 'RENTAL_MODE_DIRECT_HOST_AVAILABLE',
    pricing: 'provider-quote',
  },
  'custom-direct': {
    title: 'Rent capacity at the posted price',
    summary: 'Choose how much you need and pay the provider\'s posted price.',
    guarantee:
      'You get the resources you asked for at the price shown, from this provider. No particular machine is reserved for you.',
    mode: 'RENTAL_MODE_DIRECT_CUSTOM',
    pricing: 'provider-quote',
  },
  competitive: {
    title: 'Set a maximum price and request offers',
    summary: 'Name the most you will pay and let providers respond.',
    guarantee:
      'Every eligible provider may send one offer at or below your maximum, and you choose. Offers are public on the chain. No host or price is fixed until you accept an offer.',
    mode: 'RENTAL_MODE_COMPETITIVE',
    pricing: 'tenant-cap',
  },
}

/**
 * Why each direct product is unavailable when no host has been chosen.
 *
 * The chooser is reachable without a provider — the catalog's "Rent without
 * choosing a host" leads there, and the competitive path is exactly what that
 * is for. The three direct products need a provider to quote them, so they are
 * named as needing one up front rather than offered and then refused by
 * `pathBlocker` after the tenant has picked one and pressed Continue.
 *
 * Frozen and module-level: it is passed as a prop and never varies, so a fresh
 * object per render would only defeat memoisation.
 */
export const NO_PROVIDER_PATHS: Partial<Record<RentalPath, string>> = Object.freeze({
  'host-exclusive': 'Choose a host from the marketplace to rent a whole machine.',
  'host-available': "Choose a host from the marketplace to buy one machine's spare capacity.",
  'custom-direct': 'Choose a host from the marketplace to pay a provider\'s posted price.',
})

/** True for the three direct products. */
export function isDirect(mode: RentalMode): boolean {
  return mode !== 'RENTAL_MODE_COMPETITIVE'
}

/** True for the two products bound to one physical host. */
export function isHostProduct(mode: RentalMode): boolean {
  return (
    mode === 'RENTAL_MODE_DIRECT_HOST_EXCLUSIVE' ||
    mode === 'RENTAL_MODE_DIRECT_HOST_AVAILABLE'
  )
}

/** Why a host product cannot carry a persistent volume. */
export const VOLUME_ON_HOST_PRODUCT = 'A persistent volume needs custom capacity. Renting a whole host cannot include one.'

/** Why an open request cannot carry one yet. */
export const VOLUME_ON_OPEN_REQUEST = 'A persistent volume needs custom capacity at a host’s posted price for now. Hosts do not price volumes in open requests yet, so a request with one would get no offers.'

/**
 * Why this rental cannot include a persistent volume, or null when it can.
 *
 * Only custom capacity can. A host product is refused by the provider
 * (`persistent_storage_not_on_host_product`) and by the chain (rental code 16).
 * An open request is answered by bid engines that price a persistent volume
 * under its storage class, and the providers ops deploys price only
 * `ephemeral`, so every bid fails. No mode yet (a bare Configure) becomes an
 * open request. Allow `RENTAL_MODE_COMPETITIVE` here once providers price
 * class `default` (DDWS-com/ops#92).
 */
export function volumeUnavailableReason(mode: RentalMode | undefined): string | null {
  if (mode === 'RENTAL_MODE_DIRECT_CUSTOM') return null
  return mode !== undefined && isHostProduct(mode) ? VOLUME_ON_HOST_PRODUCT : VOLUME_ON_OPEN_REQUEST
}

/**
 * True ONLY for the exclusive product.
 *
 * `host-available` is bound to a host and is not exclusive. That distinction is
 * the single most consequential one in this module: a tenant told their shared
 * host was exclusive has been sold a guarantee nobody is providing. Every
 * caller asks this rather than re-deriving it from the mode string, so there
 * is one place to be wrong and it is tested.
 */
export function isExclusive(mode: RentalMode): boolean {
  return mode === 'RENTAL_MODE_DIRECT_HOST_EXCLUSIVE'
}

/** A host binding is required for the host products and forbidden otherwise. */
export function requiresHostOffer(mode: RentalMode): boolean {
  return isHostProduct(mode)
}

export interface RentalSelection {
  /** The path the tenant picked. */
  path: RentalPath
  /**
   * The tenant's own maximum, as a decimal DDT string. Empty means unset.
   *
   * Present for every path, not only the competitive one, because a tenant can
   * type one at any point and the mode must follow — see resolveMode.
   */
  priceCap: string
  /**
   * The opaque host offer id, for a host product.
   *
   * Explicitly `| undefined` because this repo builds with
   * `exactOptionalPropertyTypes`, under which `?:` alone means "may be absent"
   * but not "may be present and undefined". Callers legitimately pass
   * `undefined` to mean "no host chosen yet", and that is the state the
   * blocker below exists to catch.
   */
  hostOfferId?: string | undefined
  /** The provider that issued the quote or offer. */
  provider?: string | undefined
}

/**
 * The mode a selection actually is.
 *
 * A price the tenant entered forces COMPETITIVE regardless of which path they
 * started on, because that is what a tenant-set price MEANS on this protocol:
 * the request goes to every eligible provider and none of them is bound to the
 * host the tenant may have been looking at. Silently keeping a direct mode
 * would produce a review screen naming a host and a fixed price for a request
 * that has neither.
 *
 * Gate 4 requires that "entering a tenant price always switches to competitive
 * mode", and this is that rule in one place. The chooser reads it back to
 * decide what to show, so the switch is visible to the tenant rather than
 * discovered at signing.
 */
export function resolveMode(selection: RentalSelection): RentalMode {
  if (hasTenantPrice(selection.priceCap)) return 'RENTAL_MODE_COMPETITIVE'
  return RENTAL_PATH_COPY[selection.path].mode
}

/** Whether the tenant has entered a usable price of their own. */
export function hasTenantPrice(priceCap: string | undefined): boolean {
  if (!priceCap) return false
  const raw = priceCap.trim()
  if (raw === '') return false
  // A malformed entry is still an ATTEMPT to set a price, and the mode must
  // follow the attempt rather than the parse. Treating "0.0.1" as "no price"
  // would leave a tenant on a direct path with a price field they believe they
  // filled in — and the validation error they get next would be about the
  // number, not about which product they are buying.
  return /[0-9]/.test(raw)
}

/** The pricing source implied by a selection, after mode resolution. */
export function resolvePricingSource(selection: RentalSelection): PricingSource {
  return resolveMode(selection) === 'RENTAL_MODE_COMPETITIVE'
    ? 'tenant-cap'
    : RENTAL_PATH_COPY[selection.path].pricing
}

/**
 * Why a selection cannot leave the PATH CHOOSER, or null.
 *
 * Deliberately weaker than `selectionBlocker`. There are two validation points
 * in this journey and conflating them blocks the primary path with a
 * nonsensical message:
 *
 *   - the chooser knows the path, the price and (maybe) which provider the
 *     tenant came from;
 *   - the host offer id comes from that provider's own offer list, which the
 *     NEXT screen fetches.
 *
 * Demanding a host offer id here told a tenant who had just clicked "rent this
 * host" that the host was "no longer available", on a screen whose whole
 * purpose is choosing what to buy from it. That is worse than the defect it was
 * guarding against.
 */
export function pathBlocker(selection: RentalSelection): string | null {
  const mode = resolveMode(selection)

  if (mode === 'RENTAL_MODE_COMPETITIVE') {
    // Competitive requests are open by default. A tenant-entered amount still
    // resolves to the same mode (see resolveMode), but an empty amount is now
    // the deliberate no-cap product rather than an incomplete form.
    return null
  }

  return selection.provider ? null : 'Choose a provider.'
}

/**
 * Why a selection cannot be SIGNED, or null.
 *
 * The final check, including the host binding. Returned as tenant-facing copy
 * rather than a code, because every one of these is a thing the tenant can fix
 * and the fix is what they need to read.
 */
export function selectionBlocker(selection: RentalSelection): string | null {
  const mode = resolveMode(selection)

  if (mode === 'RENTAL_MODE_COMPETITIVE') {
    return null
  }

  if (!selection.provider) {
    return 'Choose a provider.'
  }

  if (requiresHostOffer(mode) && !selection.hostOfferId) {
    // The plan's §3 finding, made impossible: the old flow passed a host
    // address for display and a suggested price cap while the signed
    // deployment bound nothing. A host product without a host offer id would
    // be that same unenforced host, so it cannot proceed.
    return 'This host is no longer available. Choose another.'
  }

  return null
}

/** A quote as the console holds it, mirroring `ddws.rental.v1.DirectQuote`. */
export interface DirectQuoteView {
  quoteId: string
  provider: string
  hostOfferId: string
  rentalMode: RentalMode
  /** Integer uddt per block, as a string. */
  pricePerBlock: string
  expiryHeight: number
}

/**
 * Whether a quote is still usable at the given chain height.
 *
 * Exclusive of the expiry height, matching the chain: at `expiryHeight` the
 * quote is already dead, and a console that offered it there would send the
 * tenant into a wallet prompt for a transaction that cannot succeed.
 */
export function isQuoteLive(quote: DirectQuoteView, height: number): boolean {
  return height < quote.expiryHeight
}

/**
 * Blocks remaining on a quote, floored at zero.
 *
 * Floored rather than negative so a countdown cannot render "-4 blocks left",
 * which reads as a bug rather than as an expired quote.
 */
export function quoteBlocksRemaining(quote: DirectQuoteView, height: number): number {
  return Math.max(0, quote.expiryHeight - height)
}
